// src/pages/admin/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getPendingApplications } from '../../services/UserRolesService';
import './Admin.css';

function AdminDashboard() {
  const { currentUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Check if user is admin and redirect if not
  useEffect(() => {
    if (!loading && (!currentUser || !isAdmin)) {
      navigate('/');
    }
  }, [currentUser, isAdmin, loading, navigate]);

  // Fetch pending applications
  useEffect(() => {
    const fetchPendingApplications = async () => {
      try {
        const apps = await getPendingApplications();
        setApplications(apps);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching applications:", err);
        setError("Failed to load applications. Please try again later.");
        setLoading(false);
      }
    };

    if (currentUser && isAdmin) {
      fetchPendingApplications();
    }
  }, [currentUser, isAdmin]);

  // Format date
  const formatDate = (date) => {
    if (!date) return 'Recently';
    return new Date(date).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return <div className="admin-container">Loading admin dashboard...</div>;
  }

  if (error) {
    return <div className="admin-container error-message">{error}</div>;
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <p>Manage service provider applications and site content</p>
      </div>
      
      <div className="admin-menu">
        <Link to="/admin" className="admin-menu-item active">
          Applications
        </Link>
        <Link to="/admin/users" className="admin-menu-item">
          Users
        </Link>
        <Link to="/admin/listings" className="admin-menu-item">
          Listings
        </Link>
      </div>
      
      <div className="admin-content">
        <div className="section-header">
          <h2>Pending Service Provider Applications</h2>
          <p>{applications.length} application(s) awaiting review</p>
        </div>
        
        {applications.length === 0 ? (
          <div className="empty-state">
            <p>No pending applications at this time.</p>
          </div>
        ) : (
          <div className="applications-list">
            {applications.map(app => (
              <div key={app.id} className="application-card">
                <div className="application-header">
                  <h3>{app.businessName}</h3>
                  <span className="badge">{app.businessType}</span>
                </div>
                
                <div className="application-info">
                  <p><strong>Applicant:</strong> {app.displayName}</p>
                  <p><strong>Email:</strong> {app.email}</p>
                  <p><strong>Phone:</strong> {app.phoneNumber}</p>
                  <p><strong>Submitted:</strong> {formatDate(app.submittedAt)}</p>
                </div>
                
                <div className="application-description">
                  <p><strong>Description:</strong></p>
                  <p>{app.description}</p>
                </div>
                
                <div className="application-actions">
                  <Link to={`/admin/application/${app.id}`} className="view-button">
                    View Details
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;